import { getRouteLocaleVariant, normalizeLocale } from 'src/i18n/config'

export const JOURNAL_DOMAIN_DEFAULT_LOCALE = 'pl'

// Szablony bez prefiksu językowego - prefiks dokłada createJournalDomainRoutes
// w zależności od domyślnego języka czasopisma.
const JOURNAL_DOMAIN_ROUTE_TEMPLATES = {
  PAGES: {
    DETAIL: {
      pl: '/:slug',
      en: '/:slug',
    },
  },
  HOME: {
    pl: '/',
    en: '/',
  },
  SEARCH_RESULTS: {
    pl: '/wyniki-wyszukiwania',
    en: '/search-results',
  },
  AUTHORS: {
    DETAIL: {
      pl: '/autorzy/:slug',
      en: '/authors/:slug',
    },
  },
  ISSUES: {
    LIST: {
      pl: '/numery',
      en: '/issues',
    },
    DETAIL: {
      pl: '/numery/:issue',
      en: '/issues/:issue',
    },
    DETAIL_DETAIL: {
      pl: '/numery/:issue/:article',
      en: '/issues/:issue/:article',
    },
    EDITORIAL_BOARD: {
      pl: '/numery/:issue/sklad-redakcji',
      en: '/issues/:issue/editorial-board',
    },
    SCIENTIFIC_ADVISORY_BOARD: {
      pl: '/numery/:issue/sklad-rady-naukowej',
      en: '/issues/:issue/scientific-advisory-board',
    },
  },
  JOURNALS: {
    ARTICLE: {
      pl: '/artykuly/:article',
      en: '/articles/:article',
    },
    AHEAD_OF_PRINT: {
      pl: '/ahead-of-print',
      en: '/ahead-of-print',
    },
    EDITORIAL_BOARD: {
      pl: '/sklad-redakcji',
      en: '/editorial-board',
    },
    EDITOR_DETAIL: {
      pl: '/sklad-redakcji/:slug',
      en: '/editorial-board/:slug',
    },
    SCIENTIFIC_ADVISORY_BOARD: {
      pl: '/sklad-rady-naukowej',
      en: '/scientific-advisory-board',
    },
    COUNCIL_DETAIL: {
      pl: '/sklad-rady-naukowej/:slug',
      en: '/scientific-advisory-board/:slug',
    },
    OUR_REVIEWERS: {
      pl: '/nasi-recenzenci',
      en: '/our-reviewers',
    },
    REVIEWER_DETAIL: {
      pl: '/nasi-recenzenci/:slug',
      en: '/our-reviewers/:slug',
    },
    SUBPAGE: {
      pl: '/podstrona/:slug',
      en: '/subpage/:slug',
    },
    NEWS: {
      pl: '/aktualnosci',
      en: '/news',
    },
    NEWS_DETAIL: {
      pl: '/aktualnosci/:slug',
      en: '/news/:slug',
    },
  },
}

const isRouteTemplateMap = (value) => typeof value?.pl === 'string' && typeof value?.en === 'string'

const buildLocaleTemplate = (templates, locale, defaultLocale) => {
  const template = templates[getRouteLocaleVariant(locale)]

  if (locale === defaultLocale) {
    return template
  }

  return template === '/' ? `/${locale}` : `/${locale}${template}`
}

const mapRouteTemplates = (node, locales, defaultLocale) => {
  if (isRouteTemplateMap(node)) {
    return Object.fromEntries(locales.map((locale) => [locale, buildLocaleTemplate(node, locale, defaultLocale)]))
  }

  return Object.fromEntries(
    Object.entries(node).map(([key, value]) => [key, mapRouteTemplates(value, locales, defaultLocale)]),
  )
}

/**
 * Buduje mapy tras czasopisma: domyślny język bez prefiksu, pozostałe z prefiksem `/{locale}`.
 * Każdy liść ma jawny klucz dla każdego języka, więc buildLocalizedPath nie używa logiki wariantów.
 */
export const createJournalDomainRoutes = ({ defaultLocale = JOURNAL_DOMAIN_DEFAULT_LOCALE, locales = [] } = {}) => {
  const normalizedDefaultLocale = normalizeLocale(defaultLocale) || JOURNAL_DOMAIN_DEFAULT_LOCALE
  const normalizedLocales = [
    ...new Set(
      ['pl', 'en', normalizedDefaultLocale, ...(Array.isArray(locales) ? locales : [])]
        .map(normalizeLocale)
        .filter(Boolean),
    ),
  ]

  return mapRouteTemplates(JOURNAL_DOMAIN_ROUTE_TEMPLATES, normalizedLocales, normalizedDefaultLocale)
}

export const DEFAULT_JOURNAL_DOMAIN_ROUTES = createJournalDomainRoutes()

const collectFirstSegments = (node, variant) => {
  if (isRouteTemplateMap(node)) {
    const [segment] = node[variant].split('/').filter(Boolean)

    return segment ? [segment] : []
  }

  return Object.values(node).flatMap((child) => collectFirstSegments(child, variant))
}

const ENGLISH_ROUTE_SEGMENTS = new Set(collectFirstSegments(JOURNAL_DOMAIN_ROUTE_TEMPLATES, 'en'))
const POLISH_ONLY_ROUTE_SEGMENTS = new Set(
  collectFirstSegments(JOURNAL_DOMAIN_ROUTE_TEMPLATES, 'pl').filter((segment) => !ENGLISH_ROUTE_SEGMENTS.has(segment)),
)

// Np. `numery`, `podstrona` - pierwszy segment ścieżki istniejący tylko w polskich trasach czasopisma.
export const isPolishOnlyJournalRouteSegment = (segment) => POLISH_ONLY_ROUTE_SEGMENTS.has(segment)
